import {AsyncThunkConfig, GetThunkAPI} from '@reduxjs/toolkit/dist/createAsyncThunk'
import axios from 'axios'
import React, {FC, useState} from 'react'
import {accessSessionTokenKeyName} from 'src/utils/constants'
import useAppToast from 'src/hooks/useAppToast'
import {getValidAdminAccessToken} from 'src/utils/impersonationTokens'
import {
  isChannelSettingsSaveDebugEnabled,
  isChannelSettingsSaveDebugEndpoint,
} from 'src/utils/channelSettingsSaveDebug'
import {
  isInvalidTokenApiError,
  performAdminSessionExpiredLogout,
  performSessionExpiredLogout,
} from 'src/utils/sessionExpiredLogout'

type REQUEST_METHODS = 'GET' | 'POST' | 'PUT' | 'DELETE'
type ENDPOINT = string
// type TOOLKIT = any
type PARAMS = Object
// type HEADER_OPTIONS = any

type AxiosClientOptions = {
  /** List endpoints where 404 means no rows yet, not a hard failure */
  emptyOn404?: boolean
}

const EMPTY_PAGINATED_LIST = {results: {data: []}, count: 0}

const SUBSCRIPTION_ENDPOINTS = [
  '/api/request-subscription',
  '/api/approve-subscription',
  '/api/cancel-subscription',
  '/api/change-subscription',
]

const shouldLogSubscriptionCall = (endpoint: string) =>
  SUBSCRIPTION_ENDPOINTS.some((path) => endpoint.includes(path))

const appendSubscriptionDebugLog = (entry: Record<string, any>) => {
  try {
    const existing = JSON.parse(localStorage.getItem('subscription_api_debug') || '[]')
    const safeArray = Array.isArray(existing) ? existing.slice(-9) : [] // keep last 9
    safeArray.push(entry)
    localStorage.setItem('subscription_api_debug', JSON.stringify(safeArray))
  } catch (err) {
    console.warn('Unable to persist subscription debug log', err)
  }
}

const postSubscriptionLog = async (entry: Record<string, any>, token?: string | null) => {
  try {
    await axios.post(
      process.env.REACT_APP_API_URL + '/api/subscription-log',
      entry,
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(token ? {Authorization: `Bearer ${token}`} : {}),
        },
      }
    )
  } catch (err) {
    console.warn('Unable to persist subscription log to server', err)
  }
}

const getTokenSync = () => {
  return localStorage.getItem(accessSessionTokenKeyName)
}

const AxiosClient = async (
  method: REQUEST_METHODS,
  endPoint: ENDPOINT,
  payload: any,
  toolkit: any,
  params: PARAMS,
  signal?: AbortSignal,
  responseType?: any,
  useAdminToken?: boolean,
  options?: AxiosClientOptions
) => {
  const accessToken = getTokenSync()
  const logInAdminAccessToken = getValidAdminAccessToken()
  const token = useAdminToken ? logInAdminAccessToken : accessToken
  const { warningToast } = useAppToast()
  const shouldLog = shouldLogSubscriptionCall(endPoint)
  const debugMeta = shouldLog
    ? {method, endPoint, payload, params, timestamp: new Date().toISOString()}
    : null
  // console.log('token', token)

  const headerOptions = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    // 'xadmin': logInAdminAccessToken ?endPoint ==="/api/authenticated-user"?  null:logInAdminAccessToken: null
   'xadmin': logInAdminAccessToken ?logInAdminAccessToken: null
  }

  if (token) {
    headerOptions['Authorization'] = `Bearer ${token}`
  }

  if (
    isChannelSettingsSaveDebugEnabled() &&
    isChannelSettingsSaveDebugEndpoint(method, endPoint)
  ) {
    console.warn(
      '[MC_DEBUG_CHANNEL_SETTINGS_SAVE] Blocked API call. Remove localStorage flag to persist.',
      {method, endPoint, payload, params}
    )
    warningToast(
      'Save blocked (MC_DEBUG_CHANNEL_SETTINGS_SAVE=1). Clear the flag in DevTools to persist.'
    )
    return toolkit.rejectWithValue({
      status: 0,
      data: {message: 'Save blocked by MC_DEBUG_CHANNEL_SETTINGS_SAVE debug flag'},
      __channelSettingsSaveDebugBlocked: true,
    })
  }

  try {
    const response = await axios({
      url: process.env.REACT_APP_API_URL + endPoint,
      method: method,
      data: payload,
      params: {...params},
      headers: {...headerOptions},
      signal,
      responseType: responseType || "json"  
    })
    if (shouldLog) {
      const logEntry = {
        ...debugMeta,
        kind: 'success',
        status: response.status,
        data: response.data,
      }
      appendSubscriptionDebugLog(logEntry)
      postSubscriptionLog(logEntry, token).catch(() => {})
      console.log('[Subscription API Success]', {...debugMeta, status: response.status})
    }
     if (responseType === "blob") {
      return response;   // NOT response.data
    }
    return toolkit.fulfillWithValue(response.data)
  } catch (error:any) {
    if (
      error?.code === 'ERR_CANCELED' ||
      error?.name === 'CanceledError' ||
      error?.name === 'AbortError'
    ) {
      return toolkit.rejectWithValue({ cancelled: true })
    }
    console.log('error: axios client', error)

    const isUnauthorized = error?.response?.status === 401
    const invalidToken = isInvalidTokenApiError(error)

    if (isUnauthorized && token && useAdminToken && invalidToken) {
      performAdminSessionExpiredLogout(toolkit.dispatch)
      return toolkit.rejectWithValue(error.response)
    }

    if (isUnauthorized && token && !useAdminToken && invalidToken) {
      performSessionExpiredLogout(toolkit.dispatch)
      return toolkit.rejectWithValue(error.response)
    }

    if(error?.code==="ERR_NETWORK" && endPoint=='/api/authenticated-user' ){
      warningToast(error?.message)
    }

    if(error?.response?.status==300){
      if (shouldLog) {
        const logEntry = {
          ...debugMeta,
          kind: 'handled_error',
          status: error?.response?.status,
          data: error?.response?.data,
        }
        appendSubscriptionDebugLog(logEntry)
        postSubscriptionLog(logEntry, token).catch(() => {})
        console.warn('[Subscription API Handled Error]', {...debugMeta, status: error?.response?.status})
      }
      return toolkit.fulfillWithValue(error?.response?.data)
    }
    if (options?.emptyOn404 && error?.response?.status === 404) {
      return toolkit.fulfillWithValue(EMPTY_PAGINATED_LIST)
    }
    if (shouldLog) {
      const logEntry = {
        ...debugMeta,
        kind: 'error',
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      }
      appendSubscriptionDebugLog(logEntry)
      postSubscriptionLog(logEntry, token).catch(() => {})
      console.warn('[Subscription API Error]', {...debugMeta, status: error?.response?.status})
    }
    return toolkit.rejectWithValue(error.response)
  }
}

const publicAxiosClient = async (method: REQUEST_METHODS, endPoint: ENDPOINT, payload: any,  toolkit: any,
  params: PARAMS) => {

    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    }
  try {
    const response = await axios({
      url: process.env.REACT_APP_API_URL + endPoint,
      method: method,
      data: payload,
      headers,
      params,
    })
    return toolkit.fulfillWithValue(response.data)
    // return response.data // Return raw data
  }   catch (error:any) {
    // Extract only necessary serializable parts from the error response
    const serializableError = {
      status: error.response?.status,
      data: error.response?.data,
    };

    console.log("error in rrsponse:",serializableError)
    return toolkit.rejectWithValue(serializableError);
  }
}

export {publicAxiosClient, AxiosClient}
export default AxiosClient
